import React, { memo } from 'react'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Grid } from 'transfix-ui/core'
import { IFacilityScheduleRulesUnloadTypes, IShipmentStopTypes } from 'types/graphqlTypes'
import { findEquipment } from 'constants/equipment'
import { IAwardedLaneFragment } from '../../../../graphql/AwardedLaneFragment'
import { getAwardedLaneWeight, getAwardedLaneLoadingMethod } from '../helpers'
import { LaneDetailsRow } from './LaneDetailsRow'
import { LaneFacilityDetails } from './LaneFacilityDetails'

interface ILaneDetailsProps {
  awardedLane: IAwardedLaneFragment
}

export const LaneDetails = memo<ILaneDetailsProps>(({ awardedLane }) => {
  const {
    equipment,
    isDeliveryDropTrailer,
    isPickupDropTrailer,
    listingDetails,
    shipperRateShipperFacilities,
  } = awardedLane
  const { commodity, maxWeight, minWeight, note, offerLeadTime } = listingDetails ?? {}

  const loadingMethod = getAwardedLaneLoadingMethod(isPickupDropTrailer, isDeliveryDropTrailer)
  const { name: equipmentName } = findEquipment(equipment?.parent?.id)

  const formattedOfferLeadTime = offerLeadTime ? `Usually ${offerLeadTime} Days` : null
  const weight = getAwardedLaneWeight({
    minWeight,
    maxWeight,
  })

  return (
    <Grid container spacing={2}>
      <LaneDetailsRow
        label='Equipment Type'
        value={equipmentName}
        dataTest='lane-details-equipment'
      />
      <LaneDetailsRow
        label='Loading Method'
        value={loadingMethod}
        dataTest='lane-details-loading-method'
      />
      <LaneDetailsRow label='Commodity' value={commodity} dataTest='lane-details-commodity' />
      <LaneDetailsRow label='Weight' value={weight} dataTest='lane-details-weight' />
      <LaneDetailsRow
        label='Offer Lead Time'
        value={formattedOfferLeadTime}
        dataTest='lane-details-offer-lead-time'
      />
      <LaneDetailsRow
        label={
          <>
            <MapMarker sequence={0} type='pickup' />
            &nbsp; Pickup Facility
          </>
        }
        value={
          <LaneFacilityDetails
            dataTest='pickup-facility-details'
            shipperRateShipperFacilities={shipperRateShipperFacilities}
            stopType={IShipmentStopTypes.pickup}
            unloadType={
              isPickupDropTrailer
                ? IFacilityScheduleRulesUnloadTypes.DROP
                : IFacilityScheduleRulesUnloadTypes.LIVE
            }
          />
        }
      />
      <LaneDetailsRow
        label={
          <>
            <MapMarker sequence={0} type='delivery' />
            &nbsp; Dropoff Facility
          </>
        }
        value={
          <LaneFacilityDetails
            dataTest='delivery-facility-details'
            shipperRateShipperFacilities={shipperRateShipperFacilities}
            stopType={IShipmentStopTypes.delivery}
            unloadType={
              isDeliveryDropTrailer
                ? IFacilityScheduleRulesUnloadTypes.DROP
                : IFacilityScheduleRulesUnloadTypes.LIVE
            }
          />
        }
      />
      <LaneDetailsRow label='Additional Notes' value={note} dataTest='lane-details-note' />
    </Grid>
  )
})

export default LaneDetails
